import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import ArchiveIcon from "@mui/icons-material/Archive";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import AxiosInstance from "../../Config/AxiosInstance";
import { ApiList } from "../../Config/ApiList";
import TableContainer from "../../Components/TableContainer";

const CATEGORIES = [
    "leash_rules", "fencing", "amenities", "difficulty",
    "surface", "activity_style", "vibe",
];

const EMPTY_FORM = {
    id: null,
    slug: "",
    name: "",
    applies_to: "both",
    category: "amenities",
    is_mutually_exclusive: false,
    sort_order: 0,
    status: "active",
};

const SpotTags = () => {
    const [loading, setLoading] = useState(true);
    const [tags, setTags] = useState([]);
    const [page, setPage] = useState(0);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const pageSize = 25;

    const fetchTags = useCallback(async () => {
        setLoading(true);
        try {
            const res = await AxiosInstance.get(ApiList.SPOT_ADMIN_TAGS);
            if (res?.data?.s === 1) {
                const rows = (res.data.r.tags || []).map((t, i) => ({ ...t, rowid: i + 1 }));
                setTags(rows);
            } else {
                toast.error(res?.data?.m || "Failed to load tags");
            }
        } catch {
            toast.error("Failed to load tags");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTags();
    }, [fetchTags]);

    const openAdd = () => {
        setForm(EMPTY_FORM);
        setDialogOpen(true);
    };
    const openEdit = (t) => {
        setForm({
            id: t.id,
            slug: t.slug,
            name: t.name,
            applies_to: t.applies_to,
            category: t.category,
            is_mutually_exclusive: !!t.is_mutually_exclusive,
            sort_order: t.sort_order || 0,
            status: t.status,
        });
        setDialogOpen(true);
    };

    const saveTag = async () => {
        if (saving) return;
        const slug = form.slug.trim().toLowerCase();
        const name = form.name.trim();
        if (!/^[a-z0-9-]+$/.test(slug)) {
            toast.error("Slug must be lowercase letters, digits, hyphens");
            return;
        }
        if (name.length < 2) {
            toast.error("Name required");
            return;
        }
        setSaving(true);
        try {
            let res;
            const body = {
                slug,
                name,
                applies_to: form.applies_to,
                category: form.category,
                is_mutually_exclusive: form.is_mutually_exclusive ? 1 : 0,
                sort_order: Number(form.sort_order) || 0,
            };
            if (form.id) {
                body.status = form.status;
                res = await AxiosInstance.patch(`${ApiList.SPOT_ADMIN_TAG}/${form.id}`, body);
            } else {
                res = await AxiosInstance.post(ApiList.SPOT_ADMIN_TAGS, body);
            }
            if (res?.data?.s === 1) {
                toast.success(form.id ? "Tag updated" : "Tag created");
                setDialogOpen(false);
                fetchTags();
            } else {
                toast.error(res?.data?.m || "Save failed");
            }
        } catch {
            toast.error("Save failed");
        } finally {
            setSaving(false);
        }
    };

    const archiveTag = async (t) => {
        const result = await Swal.fire({
            title: `Archive tag "${t.name}"?`,
            text: "Existing assignments keep working; the tag just disappears from new-suggestion dropdowns.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            confirmButtonText: "Archive",
        });
        if (!result.isConfirmed) return;
        try {
            const res = await AxiosInstance.post(`${ApiList.SPOT_ADMIN_TAG_ARCHIVE}/${t.id}/archive`);
            if (res?.data?.s === 1) {
                toast.success("Tag archived");
                fetchTags();
            } else {
                toast.error(res?.data?.m || "Archive failed");
            }
        } catch {
            toast.error("Archive failed");
        }
    };

    const columns = useMemo(() => [
        { field: "rowid", headerName: "#", width: 60, sortable: false },
        { field: "name", headerName: "Name", flex: 1, minWidth: 160 },
        {
            field: "slug",
            headerName: "Slug",
            flex: 1,
            minWidth: 160,
            renderCell: (p) => (
                <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: "#000" }}>
                    {p.value}
                </Typography>
            ),
        },
        {
            field: "applies_to",
            headerName: "Applies to",
            width: 120,
            renderCell: (p) => (
                <Chip size="small" label={p.value} sx={{ background: "#b9cc0340" }} />
            ),
        },
        {
            field: "category",
            headerName: "Category",
            width: 160,
            renderCell: (p) => (
                <Typography sx={{ fontSize: 12, color: "#000" }}>
                    {p.value.replace("_", " ")}
                </Typography>
            ),
        },
        {
            field: "is_mutually_exclusive",
            headerName: "Mutex",
            width: 80,
            renderCell: (p) => (p.value ? "✓" : ""),
        },
        { field: "sort_order", headerName: "Sort", width: 70 },
        {
            field: "status",
            headerName: "Status",
            width: 110,
            renderCell: (p) => (
                <Chip
                    size="small"
                    label={p.value}
                    sx={{
                        background: p.value === "active" ? "#c6ff0060" : "#8884",
                        color: "#000",
                    }}
                />
            ),
        },
        {
            field: "actions",
            headerName: "Actions",
            width: 120,
            sortable: false,
            renderCell: (p) => (
                <Box sx={{ display: "flex" }}>
                    <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(p.row)}>
                            <EditIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                    {p.row.status === "active" && (
                        <Tooltip title="Archive">
                            <IconButton size="small" onClick={() => archiveTag(p.row)}>
                                <ArchiveIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            ),
        },
    ], []);

    return (
        <Box sx={{ width: "100%", height: "100vh", background: "#B9CC0310", overflow: "hidden" }}>
            <Box sx={{ width: "100%", p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography sx={{ fontSize: { xs: "18px", md: "28px" }, fontWeight: 300, color: "#000" }}>
                    Spot Tags
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={openAdd}
                    sx={{ background: "#c6ff00", color: "#000", fontWeight: 600, "&:hover": { background: "#b5ee00" } }}
                >
                    Add Tag
                </Button>
            </Box>

            <Box sx={{ width: "100%", height: "calc(100vh - 90px)", p: 2 }}>
                <TableContainer
                    columns={columns}
                    rows={tags}
                    loading={loading}
                    page={page}
                    handlePageChange={setPage}
                    totalData={tags.length}
                    pageSize={pageSize}
                />
            </Box>

            <Dialog open={dialogOpen} onClose={saving ? undefined : () => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{form.id ? "Edit Tag" : "Add Tag"}</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth margin="normal" label="Name"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                    />
                    <TextField
                        fullWidth margin="normal" label="Slug"
                        helperText="Lowercase letters, digits, and hyphens only"
                        value={form.slug}
                        onChange={(e) => setForm({ ...form, slug: e.target.value })}
                    />
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Applies to</InputLabel>
                        <Select
                            value={form.applies_to}
                            label="Applies to"
                            onChange={(e) => setForm({ ...form, applies_to: e.target.value })}
                        >
                            <MenuItem value="both">Both</MenuItem>
                            <MenuItem value="park">Park</MenuItem>
                            <MenuItem value="trail">Trail</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Category</InputLabel>
                        <Select
                            value={form.category}
                            label="Category"
                            onChange={(e) => setForm({ ...form, category: e.target.value })}
                        >
                            {CATEGORIES.map((c) => (
                                <MenuItem key={c} value={c}>{c.replace("_", " ")}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        fullWidth margin="normal" type="number" label="Sort order"
                        value={form.sort_order}
                        onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={form.is_mutually_exclusive}
                                onChange={(e) => setForm({ ...form, is_mutually_exclusive: e.target.checked })}
                            />
                        }
                        label="Mutually exclusive within its category (only one per spot)"
                    />
                    {form.id && (
                        <FormControl fullWidth margin="normal">
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={form.status}
                                label="Status"
                                onChange={(e) => setForm({ ...form, status: e.target.value })}
                            >
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="archived">Archived</MenuItem>
                            </Select>
                        </FormControl>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
                    <Button
                        onClick={saveTag}
                        variant="contained"
                        disabled={saving}
                        sx={{ background: "#c6ff00", color: "#000", fontWeight: 600, "&:hover": { background: "#b5ee00" } }}
                    >
                        {saving ? "Saving…" : form.id ? "Update" : "Create"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SpotTags;
